import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import fetchApi from '../utils/api';
import Swal from 'sweetalert2';
import AirDatePicker from '../components/ui/AirDatePicker';

const formatToYYYYMMDD = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getDayName = (dateStr) => {
    if (!dateStr) return '';
    try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        return days[date.getDay()];
    } catch {
        return '';
    }
};

function EditPurchaseReturnForm({ purchaseReturn, onClose, onSaved }) {
    const [submitting, setSubmitting] = useState(false);

    // Initial state setup loaded from purchaseReturn
    const [paymentType, setPaymentType] = useState('Debit');
    const [invoiceTypeName, setInvoiceTypeName] = useState('Tax invoice');
    const [sellerNameDisplay, setSellerNameDisplay] = useState('');
    const [saleNoDisplay, setSaleNoDisplay] = useState('');
    const [purchaseDate, setPurchaseDate] = useState('');
    const [returnNo, setReturnNo] = useState('');
    const [returnDate, setReturnDate] = useState('');
    const [currentTime, setCurrentTime] = useState('');

    // Items
    const [items, setItems] = useState([]);

    // Footer
    const [insurancePct, setInsurancePct] = useState(0);
    const [extraChargesInput, setExtraChargesInput] = useState(0);

    const [note, setNote] = useState('');
    const [narration, setNarration] = useState('');

    const [originalInvoice, setOriginalInvoice] = useState(null);

    // Populate data when purchaseReturn is passed
    useEffect(() => {
        if (!purchaseReturn) return;

        setPaymentType(purchaseReturn.payment_type || 'Debit');
        setInvoiceTypeName(purchaseReturn.invoice_type_name || 'Tax invoice');
        setSellerNameDisplay(purchaseReturn.seller_trade_name || purchaseReturn.seller_name || '');
        setSaleNoDisplay(purchaseReturn.original_invoice_no || '');
        setPurchaseDate(purchaseReturn.sale_date ? formatToYYYYMMDD(purchaseReturn.sale_date) : '');
        setReturnNo(purchaseReturn.return_no || '');
        setReturnDate(purchaseReturn.return_date ? formatToYYYYMMDD(purchaseReturn.return_date) : '');
        setNote(purchaseReturn.note || '');
        setNarration(purchaseReturn.narration || '');
        
        // Parse items
        let rawItems = [];
        if (Array.isArray(purchaseReturn.items)) {
            rawItems = purchaseReturn.items;
        } else if (typeof purchaseReturn.items === 'string') {
            try {
                rawItems = JSON.parse(purchaseReturn.items);
            } catch (e) {
                rawItems = [];
            }
        }
        
        setItems(rawItems.map(it => ({
            category: it.category || '',
            product: it.product || it.productName || it.name || '',
            hsnCode: it.hsnCode || it.hsn_code || '',
            grade: it.grade || '',
            color: it.color || '',
            unit: it.unit || 'PCS',
            qty: parseFloat(it.qty || it.quantity || 0),
            rate: parseFloat(it.rate || 0),
            amount: parseFloat(it.amount || it.total || 0),
        })));

        // Load original invoice details for original tax rates
        if (purchaseReturn.original_invoice_id) {
            fetchApi(`/purchase-return/invoices-by-seller?sellerId=${purchaseReturn.seller_id}`)
                .then(res => {
                    const match = (res.invoices || []).find(inv => inv.id === purchaseReturn.original_invoice_id);
                    if (match) {
                        setOriginalInvoice(match);
                        
                        // Recalculate insurance percentage based on original invoice subtotal
                        const invoiceInsurance = parseFloat(match.insurance) || 0;
                        const invoiceExtraCharges = parseFloat(match.extraChargesAmount) || 0;
                        
                        // Setup insurance percent
                        const savedInsurance = parseFloat(purchaseReturn.insurance) || 0;
                        if (savedInsurance > 0) {
                            const subTotalVal = rawItems.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
                            const calculatedPct = Math.round((savedInsurance / (subTotalVal || 1)) * 100);
                            setInsurancePct(calculatedPct || 0);
                        } else {
                            setInsurancePct(0);
                        }
                        
                        setExtraChargesInput(parseFloat(purchaseReturn.extra_charges_amount) || 0);
                    }
                })
                .catch(err => console.error("Error loading invoice for edit:", err));
        } else {
            // Setup from return amounts direct
            setExtraChargesInput(parseFloat(purchaseReturn.extra_charges_amount) || 0);
        }

        if (purchaseReturn.time) {
            setCurrentTime(purchaseReturn.time);
        }
    }, [purchaseReturn]);

    // Qty change (Only qty can be changed for return)
    const updateItemQty = (idx, val) => {
        setItems(prev => prev.map((it, i) => {
            if (i !== idx) return it;
            const qty = parseFloat(val) || 0;
            return { ...it, qty, amount: parseFloat((qty * it.rate).toFixed(2)) };
        }));
    };

    const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

    // Totals calculations
    const subTotal = items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
    const insAmt = parseFloat(((subTotal * (parseFloat(insurancePct) || 0)) / 100).toFixed(2));
    const extrAmt = parseFloat(extraChargesInput) || 0;

    const taxableAmount = parseFloat((subTotal + insAmt + extrAmt).toFixed(2));

    // GST calculation (using original invoice tax rates or 9% default)
    const tax1Str = originalInvoice?.saleTypeTax1 !== undefined ? originalInvoice.saleTypeTax1 : purchaseReturn?.sale_type_tax1;
    const tax2Str = originalInvoice?.saleTypeTax2 !== undefined ? originalInvoice.saleTypeTax2 : purchaseReturn?.sale_type_tax2;
    const tax1 = parseFloat(tax1Str !== undefined && tax1Str !== null && tax1Str !== '' ? tax1Str : 0);
    const tax2 = parseFloat(tax2Str !== undefined && tax2Str !== null && tax2Str !== '' ? tax2Str : 0);
    const centralTax = parseFloat(((taxableAmount * tax1) / 100).toFixed(2));
    const stateTax = parseFloat(((taxableAmount * tax2) / 100).toFixed(2));
    const billAmount = parseFloat((taxableAmount + centralTax + stateTax).toFixed(2));

    const handleSubmit = async () => {

    const conf = await Swal.fire({
      title: 'Save Data?',
      text: 'Are you sure you want to save this data?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#00334e',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Save',
      cancelButtonText: 'No, Keep Editing'
    });
    if(!conf.isConfirmed) return;
        if (!returnNo) { toast.error('Return number is required'); return; }
        if (items.length === 0) { toast.error('At least one item is required'); return; }
        setSubmitting(true);
        try {
            await fetchApi(`/purchase-return/${purchaseReturn.id || purchaseReturn._id}`, {
                method: 'PUT',
                body: JSON.stringify({
                    returnNo,
                    returnDate,
                    paymentType,
                    items,
                    insurance: insAmt,
                    extraChargesAmount: extrAmt,
                    subTotal,
                    taxableAmount,
                    centralTax,
                    stateTax,
                    totalAmount: billAmount,
                    note,
                    narration,
                })
            });
            toast.success('Purchase return updated successfully!');
            if (onSaved) onSaved();
            if (onClose) onClose();
        } catch (err) {
            toast.error(err.message || 'Failed to update purchase return');
        } finally {
            setSubmitting(false);
        }
    };

    // Shared Miracle style styles
    const thStyle = {
        backgroundColor: '#1e3a5f',
        color: '#ffffff',
        fontSize: '12px',
        fontWeight: '500',
        padding: '6px 8px',
        borderRight: '1px solid #365072',
        borderBottom: '1px solid #c2c2c2',
        textAlign: 'left'
    };

    const tdStyle = {
        padding: '2px 4px',
        borderRight: '1px solid #e1e5e8',
        borderBottom: '1px solid #e1e5e8',
        background: '#ffffff'
    };

    const cellInputStyle = {
        width: '100%',
        border: 'none',
        background: 'transparent',
        fontSize: '13px',
        height: '24px',
        outline: 'none',
        color: '#000000',
        padding: '0 4px',
        boxSizing: 'border-box'
    };

    return (
        <div className="edit-purchase-return-form" style={{ background: '#f8fafc', borderRadius: '12px', padding: '28px', maxWidth: '1250px', margin: '20px auto', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', fontFamily: 'Arial, sans-serif' }}>
            
            {/* Header Title */}
            <div style={{ borderBottom: '2px solid #e2e8f0', paddingBottom: '16px', marginBottom: '24px' }}>
                <h2 style={{ textAlign: 'center', fontSize: '26px', fontWeight: '800', color: '#0f172a', margin: 0, letterSpacing: '-0.5px' }}>Edit Purchase Return</h2>
            </div>

            {/* Header Inputs Section */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '24px' }}>
                {/* Cash/Debit & Party */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div>
                        <label style={{ fontSize: '13px', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '6px' }}>Cash/Debit</label>
                        <select value={paymentType} onChange={e => setPaymentType(e.target.value)} style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', background: '#fff', color: '#1e293b', fontWeight: '500' }}>
                            <option value="Cash">Cash</option>
                            <option value="Debit">Debit</option>
                        </select>
                    </div>

                    <div>
                        <label style={{ fontSize: '13px', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '6px' }}>Party A/c.</label>
                        <input
                            type="text"
                            value={sellerNameDisplay}
                            disabled
                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', background: '#f1f5f9', color: '#64748b', fontWeight: '500', cursor: 'not-allowed' }}
                        />
                    </div>
                </div>

                {/* Invoice Type & Time */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div>
                        <label style={{ fontSize: '13px', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '6px' }}>Invoice Type</label>
                        <input
                            type="text"
                            value={invoiceTypeName}
                            disabled
                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', background: '#f1f5f9', color: '#64748b', fontWeight: '500', cursor: 'not-allowed' }}
                        />
                    </div>

                    <div>
                        <label style={{ fontSize: '13px', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '6px' }}>Time</label>
                        <input
                            type="text"
                            value={currentTime}
                            disabled
                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', background: '#f8fafc', color: '#475569', fontWeight: '500' }}
                        />
                    </div>
                </div>

                {/* Purchase No, Purchase Date, Return No, Return Date */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: '#475569', width: '90px' }}>Purchase No</span>
                        <input
                            type="text"
                            value={saleNoDisplay}
                            disabled
                            style={{ flex: 1, padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', background: '#f1f5f9', color: '#64748b', fontWeight: '500', cursor: 'not-allowed' }}
                        />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: '#475569', width: '90px' }}>Purchase Date</span>
                        <input
                            type="text"
                            value={purchaseDate}
                            disabled
                            style={{ flex: 1, padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', background: '#f1f5f9', color: '#64748b', fontWeight: '500', cursor: 'not-allowed' }}
                        />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: '#475569', width: '90px' }}>Return No</span>
                        <input
                            value={returnNo}
                            disabled
                            style={{ flex: 1, padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', background: '#f1f5f9', color: '#64748b', fontWeight: '600', cursor: 'not-allowed' }}
                        />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: '#475569', width: '90px' }}>Return Date</span>
                        <div style={{ display: 'flex', gap: '6px', flex: 1 }}>
                            <div style={{ flex: 1 }}>
                                <AirDatePicker
                                    value={returnDate}
                                    onChange={(dateStr) => setReturnDate(dateStr)}
                                    style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', background: '#fff', color: '#1e293b', fontWeight: '500' }}
                                />
                            </div>
                            <div style={{ padding: '7px 10px', background: '#e2e8f0', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', fontWeight: '600', color: '#475569', minWidth: '40px', textAlign: 'center' }}>
                                {getDayName(returnDate)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Product Table */}
            <div style={{ border: '1px solid #c2c2c2', borderRadius: '6px', overflow: 'hidden', background: '#fff', marginBottom: '24px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={{ ...thStyle, width: '40px', textAlign: 'center' }}>S.N.</th>
                            <th style={{ ...thStyle, width: '140px' }}>Category</th>
                            <th style={{ ...thStyle, width: '220px' }}>Product Name</th>
                            <th style={{ ...thStyle, width: '90px' }}>HSN Code</th>
                            <th style={{ ...thStyle, width: '80px' }}>Grade</th>
                            <th style={{ ...thStyle, width: '70px' }}>Color</th>
                            <th style={{ ...thStyle, width: '70px' }}>Unit</th>
                            <th style={{ ...thStyle, width: '80px', textAlign: 'center' }}>Qty</th>
                            <th style={{ ...thStyle, width: '100px', textAlign: 'right' }}>Rate</th>
                            <th style={{ ...thStyle, width: '120px', textAlign: 'right' }}>Amount</th>
                            <th style={{ ...thStyle, width: '50px', textAlign: 'center' }}>Act</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 ? (
                            <tr>
                                <td colSpan={11} style={{ textAlign: 'center', padding: '12px', fontSize: '13px', color: '#64748b' }}>No products loaded.</td>
                            </tr>
                        ) : (
                            items.map((item, idx) => (
                                <tr key={idx}>
                                    {/* SN */}
                                    <td style={{ ...tdStyle, textAlign: 'center', fontSize: '13px', color: '#64748b' }}>{idx + 1}</td>
                                    
                                    {/* Category (Disabled) */}
                                    <td style={tdStyle}>
                                        <input type="text" value={item.category} disabled style={{ ...cellInputStyle, cursor: 'not-allowed', color: '#64748b' }} />
                                    </td>
                                    
                                    {/* Product Name (Disabled) */}
                                    <td style={tdStyle}>
                                        <input type="text" value={item.product} disabled style={{ ...cellInputStyle, cursor: 'not-allowed', color: '#64748b' }} />
                                    </td>
                                    
                                    {/* HSN Code (Disabled) */}
                                    <td style={tdStyle}>
                                        <input type="text" value={item.hsnCode} disabled style={{ ...cellInputStyle, cursor: 'not-allowed', color: '#64748b' }} />
                                    </td>
                                    
                                    {/* Grade */}
                                    <td style={tdStyle}>
                                        <input type="text" value={item.grade} disabled style={{ ...cellInputStyle, cursor: 'not-allowed', color: '#64748b' }} />
                                    </td>
                                    
                                    {/* Color */}
                                    <td style={tdStyle}>
                                        <input type="text" value={item.color} disabled style={{ ...cellInputStyle, cursor: 'not-allowed', color: '#64748b' }} />
                                    </td>
                                    
                                    {/* Unit */}
                                    <td style={tdStyle}>
                                        <input type="text" value={item.unit} disabled style={{ ...cellInputStyle, cursor: 'not-allowed', color: '#64748b' }} />
                                    </td>
                                    
                                    {/* Qty (Editable) */}
                                    <td style={{ ...tdStyle, background: '#fef08a' }}>
                                        <input
                                            type="number"
                                            value={item.qty}
                                            onChange={e => updateItemQty(idx, e.target.value)}
                                            style={{ ...cellInputStyle, textAlign: 'center', fontWeight: 'bold' }}
                                        />
                                    </td>
                                    
                                    {/* Rate */}
                                    <td style={tdStyle}>
                                        <input
                                            type="number"
                                            value={item.rate}
                                            disabled
                                            style={{ ...cellInputStyle, textAlign: 'right', cursor: 'not-allowed', color: '#64748b' }}
                                        />
                                    </td>
                                    
                                    {/* Amount */}
                                    <td style={{ ...tdStyle, paddingRight: '8px', textAlign: 'right', fontSize: '13px', fontWeight: 'bold' }}>
                                        ₹{item.amount.toFixed(2)}
                                    </td>
                                    
                                    {/* Action (Remove item) */}
                                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                                        <button type="button" onClick={() => removeItem(idx)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#ef4444', padding: 0 }}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="3 6 5 6 21 6"></polyline>
                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                            </svg>
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Bottom Calculations and Terms */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '40px', background: '#fff', padding: '24px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                {/* Notes and Terms */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div>
                        <label style={{ fontSize: '13px', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '6px' }}>Special Note</label>
                        <textarea value={note} onChange={e => setNote(e.target.value)} rows="3" style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', outline: 'none', resize: 'vertical' }} />
                    </div>

                    <div>
                        <label style={{ fontSize: '13px', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '6px' }}>Terms & Conditions</label>
                        <textarea value={narration} onChange={e => setNarration(e.target.value)} rows="5" style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', outline: 'none', resize: 'vertical' }} />
                    </div>
                </div>

                {/* Final Calculation Panel */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', background: '#f8fafc', padding: '20px', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
                    {/* Sub Total */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#475569', fontWeight: '500' }}>
                        <span>Sub Total</span>
                        <span style={{ fontWeight: '600' }}>₹{subTotal.toFixed(2)}</span>
                    </div>

                    {/* Insurance */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '14px', color: '#475569', fontWeight: '500' }}>Insurance (%)</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                                type="number"
                                value={insurancePct}
                                onChange={e => setInsurancePct(e.target.value)}
                                style={{ width: '60px', padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '13px', textAlign: 'center' }}
                            />
                            <span style={{ fontWeight: '600', fontSize: '14px', width: '70px', textAlign: 'right' }}>₹{insAmt.toFixed(2)}</span>
                        </div>
                    </div>

                    {/* Extra Charges */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '14px', color: '#475569', fontWeight: '500' }}>Extra Charges</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                                type="number"
                                value={extraChargesInput}
                                onChange={e => setExtraChargesInput(e.target.value)}
                                style={{ width: '60px', padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '13px', textAlign: 'center' }}
                            />
                            <span style={{ fontWeight: '600', fontSize: '14px', width: '70px', textAlign: 'right' }}>₹{extrAmt.toFixed(2)}</span>
                        </div>
                    </div>

                    <div style={{ borderBottom: '1px solid #cbd5e1', margin: '8px 0' }}></div>

                    {/* Taxable Amount */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#1e293b', fontWeight: '600' }}>
                        <span>Taxable Amount</span>
                        <span>₹{taxableAmount.toFixed(2)}</span>
                    </div>

                    {/* CGST */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#475569' }}>
                        <span>CGST ({tax1}%)</span>
                        <span style={{ fontWeight: '500' }}>₹{centralTax.toFixed(2)}</span>
                    </div>

                    {/* SGST */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#475569' }}>
                        <span>SGST ({tax2}%)</span>
                        <span style={{ fontWeight: '500' }}>₹{stateTax.toFixed(2)}</span>
                    </div>

                    <div style={{ borderBottom: '2px solid #cbd5e1', margin: '8px 0' }}></div>

                    {/* Final Net Bill Amount */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '18px', color: '#0f172a', fontWeight: '800' }}>
                        <span>Total Amount</span>
                        <span style={{ color: '#1e3a5f' }}>₹{billAmount.toFixed(2)}</span>
                    </div>
                </div>
            </div>

            {/* Action Form Buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px', borderTop: '2px solid #e2e8f0', paddingTop: '20px' }}>
                <button
                    onClick={() => onClose && onClose()}
                    style={{ padding: '10px 24px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: '6px', fontSize: '14px', fontWeight: '600', color: '#64748b', cursor: 'pointer' }}
                >
                    Cancel
                </button>
                <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    style={{ padding: '10px 28px', border: 'none', background: '#1e3a5f', borderRadius: '6px', fontSize: '14px', fontWeight: '600', color: '#fff', cursor: 'pointer', opacity: submitting ? 0.7 : 1 }}
                >
                    {submitting ? 'Updating...' : 'Update Return'}
                </button>
            </div>
        </div>
    );
}

export default EditPurchaseReturnForm;
